"use client"

import { BOOK_DEMO_URL } from "../../config"
import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { LocaleLink } from "../locale-link"
import { LocaleSwitcher } from "../locale-switcher"
import { CalendarClock, Linkedin } from "lucide-react"
import Image from "next/image"

import { Button } from "@/components/ui/button"

const FOOTER_T = {
  tagline: {
    FR: "Prospection B2B par agents IA. N'importe quel métier, n'importe où.",
    EN: "B2B prospecting by AI agents. Any trade, anywhere.",
  },
  col_product: { FR: "Produit", EN: "Product" },
  col_resources: { FR: "Ressources", EN: "Resources" },
  link_how: { FR: "Comment ça marche", EN: "How it works" },
  link_plugins: { FR: "Plugins", EN: "Plugins" },
  link_pricing: { FR: "Tarifs", EN: "Pricing" },
  link_demo: { FR: "Démo", EN: "Demo" },
  link_blog: { FR: "Blog", EN: "Blog" },
  link_mcp: { FR: "Assistant IA (MCP)", EN: "AI assistant (MCP)" },
  link_case_studies: {
    FR: "Cas clients",
    EN: "Case studies",
  },
  link_faq: { FR: "FAQ", EN: "FAQ" },
  link_compliance: { FR: "Conformité RGPD", EN: "GDPR compliance" },
  link_about: { FR: "À propos", EN: "About" },
  link_contact: { FR: "Contact", EN: "Contact" },
  link_terms: { FR: "CGU", EN: "Terms of service" },
  link_privacy: { FR: "Confidentialité", EN: "Privacy" },
  link_legal: { FR: "Mentions légales", EN: "Legal notice" },
  link_prospect_notice: {
    FR: "Vos données de prospection",
    EN: "Your prospecting data",
  },
  copyright: {
    FR: "© 2026 AutoLeads par Grezy. Tous droits réservés.",
    EN: "© 2026 AutoLeads by Grezy. All rights reserved.",
  },
  social_linkedin_tom: {
    FR: "LinkedIn Tom Jeannesson",
    EN: "LinkedIn Tom Jeannesson",
  },
  social_linkedin_nico: {
    FR: "LinkedIn Nicolas Faglin",
    EN: "LinkedIn Nicolas Faglin",
  },
  nav_legal: { FR: "Liens légaux", EN: "Legal" },
  footer_heading: { FR: "Pied de page", EN: "Footer" },
  cta_title: {
    FR: "Prêt à voir AutoLeads sur vos cibles ?",
    EN: "Ready to see AutoLeads on your targets?",
  },
  cta_demo: { FR: "Réserver une démo", EN: "Book a demo" },
} satisfies FullTranslation

type FooterKey = keyof typeof FOOTER_T
type FooterLink = { key: FooterKey; href: string }

export function Footer() {
  const tr = useTr(FOOTER_T)

  const PRODUCT_LINKS: FooterLink[] = [
    { key: "link_how", href: "/#how-it-works" },
    { key: "link_plugins", href: "/#plugins" },
    { key: "link_pricing", href: "/#pricing" },
    { key: "link_demo", href: "/demo" },
    { key: "link_mcp", href: "/mcp" },
  ]

  const RESOURCE_LINKS: FooterLink[] = [
    { key: "link_blog", href: "/blog" },
    { key: "link_case_studies", href: "/cas-clients" },
    { key: "link_faq", href: "/#faq" },
    { key: "link_compliance", href: "/#compliance" },
    { key: "link_about", href: "/about" },
    { key: "link_contact", href: "/contact" },
  ]

  const LEGAL_LINKS: FooterLink[] = [
    { key: "link_legal", href: "/legal" },
    { key: "link_privacy", href: "/privacy" },
    { key: "link_terms", href: "/terms" },
    // Article 14 notice: a prospected person must be able to find the way out
    // from any page, not only from the link in the email they received.
    { key: "link_prospect_notice", href: "/donnees-prospects" },
  ]

  const SOCIAL_LINKS = [
    {
      key: "social_linkedin_tom",
      href: "https://www.linkedin.com/in/tomjeannesson",
      Icon: Linkedin,
    },
    {
      key: "social_linkedin_nico",
      href: "https://www.linkedin.com/in/nfaglin",
      Icon: Linkedin,
    },
  ] as const

  const linkClass =
    "text-muted-foreground hover:text-foreground text-2sm transition-colors"

  return (
    <footer
      className="bg-muted/30 border-border border-t"
      role="contentinfo"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        {tr("footer_heading")}
      </h2>
      <div className="container py-12">
        <div className="border-border mb-10 flex flex-col items-start justify-between gap-4 border-b pb-10 sm:flex-row sm:items-center">
          <p className="text-foreground font-display tracking-snug max-w-md text-lg font-semibold text-balance sm:text-xl">
            {tr("cta_title")}
          </p>
          <a href={BOOK_DEMO_URL} target="_blank" rel="noreferrer">
            <Button
              variant="cta"
              shape="lg"
              size="lg"
              className="h-11 px-6 text-sm font-medium"
            >
              <CalendarClock className="mr-1.5 size-4" aria-hidden />
              {tr("cta_demo")}
            </Button>
          </a>
        </div>
        <div className="grid grid-cols-2 gap-10 md:grid-cols-3 md:gap-8">
          <div className="col-span-2 flex flex-col gap-4 md:col-span-1">
            <div className="font-display flex items-center gap-2 text-base font-semibold">
              <Image
                src="/grezy/grezy.svg"
                alt="Grezy"
                width={44}
                height={24}
                className="h-6 w-auto dark:invert"
              />
              <span>
                AutoLeads
                <span className="text-muted-foreground ml-1 font-normal">
                  by Grezy
                </span>
              </span>
            </div>
            <p className="text-muted-foreground text-2sm leading-relaxed">
              {tr("tagline")}
            </p>
            <div className="flex items-center gap-2">
              {SOCIAL_LINKS.map(({ key, href, Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={tr(key)}
                  className="text-muted-foreground hover:text-foreground hover:bg-card border-border inline-flex size-8 items-center justify-center rounded-md border transition-colors"
                >
                  <Icon className="size-3.5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <FooterColumn
            label={tr("col_product")}
            links={PRODUCT_LINKS}
            renderLabel={tr}
            linkClass={linkClass}
          />
          <FooterColumn
            label={tr("col_resources")}
            links={RESOURCE_LINKS}
            renderLabel={tr}
            linkClass={linkClass}
          />
        </div>

        <div className="border-border mt-12 flex flex-col items-start gap-4 border-t pt-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
            <p className="text-muted-foreground text-xs">{tr("copyright")}</p>
            <nav
              aria-label={tr("nav_legal")}
              className="flex flex-wrap items-center gap-x-4 gap-y-1"
            >
              {LEGAL_LINKS.map((link) => (
                <LocaleLink
                  key={link.key}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                >
                  {tr(link.key)}
                </LocaleLink>
              ))}
            </nav>
          </div>
          <LocaleSwitcher className="text-muted-foreground hover:text-foreground text-xs transition-colors" />
        </div>
      </div>
    </footer>
  )
}

type FooterColumnProps = {
  label: string
  links: FooterLink[]
  renderLabel: (key: FooterKey) => string
  linkClass: string
}

function FooterColumn({
  label,
  links,
  renderLabel,
  linkClass,
}: FooterColumnProps) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-foreground text-2sm font-semibold tracking-tight">
        {label}
      </h3>
      <nav aria-label={label} className="flex flex-col items-start gap-2.5">
        {links.map((link) => (
          <LocaleLink key={link.key} href={link.href} className={linkClass}>
            {renderLabel(link.key)}
          </LocaleLink>
        ))}
      </nav>
    </div>
  )
}
